import invariant from 'tiny-invariant'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { mapObject } from 'lib/object/objectUtils'

import { mapInclusion, mapPromotion, mapRateTotals } from './propertyRatesMapper'
import { mapOptional } from './propertySharedMapper'

/* ************** */
/* Pseudo-mappers */
/* ************** */

function mapDisplayPriceAsRateTotals(displayPrice: number, valuedUpToPrice?: number): App.PropertyRateTotals {
  return {
    taxesAndFees: 0,
    propertyFees: 0,
    beforeTaxesFeesPrice: displayPrice,
    sellingPrice: displayPrice,
    baseSellingPrice: displayPrice,
    displayPrice,
    valuedUpToPrice: valuedUpToPrice ?? displayPrice,
    societeHotelPrice: displayPrice,
    discount: 0,
  }
}

/* *********** */
/* Sub-mappers */
/* *********** */

type ServerAnytimePromotionRate = definitions['AnytimePromotionRate']
type ServerAnytimeRate = definitions['AnytimeRate']
type ServerAnytimeRoomRate = definitions['AnytimeRoomRate']

function mapAnytimePromotionRate({
  total,
  luxPlus,
  los,
  // roomId, // This is unused.
  // rateId, // This is unused.
  // packageId, // This is unused.
  internalService,
  promotion,
  inclusions,
  bundleInclusions,
  bonusInclusions,
  luxPlusInclusions,
}: ServerAnytimePromotionRate, currency: string): App.Property.AnytimePromotionRate {
  return {
    /* Details */
    duration: los,
    currency,
    /* Inclusions */
    promotion: mapPromotion(promotion),
    inclusions: inclusions.map(serverInclusion => mapInclusion(internalService, serverInclusion, 'normal')),
    bonusInclusions: bonusInclusions.map(serverInclusion => mapInclusion(internalService, serverInclusion, 'bonus')),
    luxPlusInclusions: luxPlusInclusions.map(serverInclusion => mapInclusion(internalService, serverInclusion, 'luxPlus')),
    // NB: For bundle inclusions, we hard-code `serverRateType` to `'bedbank'` to avoid further
    // processing via `getOfferInclusionDescriptionFromMarkdown`.
    bundleInclusions: bundleInclusions.map(serverInclusion => mapInclusion('bedbank', serverInclusion, 'normal')),
    /* Pricing */
    totals: mapRateTotals(total),
    luxPlusTotals: luxPlus && mapRateTotals(luxPlus),
  }
}

function mapAnytimeRate({
  total,
  luxPlus,
  los,
  // roomId, // This is unused.
  // rateId, // This is unused.
  // packageId, // This is unused.
  // internalService, // This is unused.
}: ServerAnytimeRate, currency: string): App.Property.AnytimeRate {
  return {
    /* Details */
    duration: los,
    currency,
    /* Pricing */
    totals: mapRateTotals(total),
    luxPlusTotals: luxPlus && mapRateTotals(luxPlus),
  }
}

function mapAnytimeRoomRate({
  displayPrice,
  luxPlusDisplayPrice,
  los,
  roomId,
  // rateId, // This is unused.
  // internalService, // This is unused.
  // checkInDate, // This is unused.
}: ServerAnytimeRoomRate, currency: string): App.Property.AnytimeRoomRate {
  // The contract types `roomId` as optional, but in reality it's always set (to the accommodation
  // service room ID).
  invariant(roomId)

  return {
    /* Identifiers */
    roomId,
    /* Details */
    duration: los,
    currency,
    /* Pricing */
    totals: mapDisplayPriceAsRateTotals(displayPrice),
    luxPlusTotals: mapOptional(luxPlusDisplayPrice, x => mapDisplayPriceAsRateTotals(x, displayPrice)),
  }
}

/* *********** */
/* Main mapper */
/* *********** */

type ServerAnytimePricingForProperty = definitions['AnytimePricingForProperty']

// NB: This mapper (as well as transitive mappers and related Customer Portal types) was last
// cross-checked against `contract-svc-accommodation` v22.0.1.
export default function mapPropertyAnytimeRate({
  lowestPrice,
  lowestPromotionPrice,
  rooms,
}: ServerAnytimePricingForProperty, currency: string): {
  anytimeRate: App.Property.AnytimeRate
  anytimePromotionRate: App.Property.AnytimePromotionRate | undefined
  anytimeRoomRates: { [roomId: string]: App.Property.AnytimeRoomRate } | undefined
} {
  return {
    anytimeRate: mapAnytimeRate(lowestPrice, currency),
    anytimePromotionRate: mapOptional(lowestPromotionPrice, x => mapAnytimePromotionRate(x, currency)),
    anytimeRoomRates: mapOptional(rooms, x => mapObject(x, x => mapAnytimeRoomRate(x, currency))),
  }
}
